import React from "react";
import { Button, Popconfirm, Tooltip } from "antd";
import type { ButtonProps } from "antd";

type ConfirmConfig = {
  title: React.ReactNode;
  okText?: React.ReactNode;
  okButtonProps?: ButtonProps;
  onConfirm: () => void;
};

type AccessControlledButtonProps = {
  canPerform: boolean;
  label: React.ReactNode;
  onClick?: (e?: any) => void;
  tooltipTitle?: React.ReactNode;
  confirm?: ConfirmConfig;
  loading?: boolean;
  submitting?: boolean;
  buttonProps?: ButtonProps;
};

export const AccessControlledButton: React.FC<AccessControlledButtonProps> = ({
  canPerform,
  label,
  onClick,
  tooltipTitle,
  confirm,
  loading,
  submitting,
  buttonProps,
}) => {
  const isBusy = !!loading || !!submitting;
  const derivedDisabled =
    !canPerform || isBusy || !!buttonProps?.disabled;

  // Not allowed → disabled + tooltip
  if (!canPerform) {
    return (
      <Tooltip title={tooltipTitle}>
        <span>
          <Button {...buttonProps} disabled>
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  // Allowed + confirm → wrap with Popconfirm
  if (confirm) {
    return (
      <Popconfirm
        title={confirm.title}
        onConfirm={confirm.onConfirm}
        okText={confirm.okText}
        okButtonProps={confirm.okButtonProps}
        disabled={!!loading}
      >
        <Button
          {...buttonProps}
          disabled={derivedDisabled}
          loading={isBusy}
          onClick={onClick}
        >
          {label}
        </Button>
      </Popconfirm>
    );
  }

  // Allowed + no confirm → plain button
  return (
    <Button
      {...buttonProps}
      disabled={derivedDisabled}
      loading={isBusy}
      onClick={onClick}
    >
      {label}
    </Button>
  );
};

export default AccessControlledButton;
